<template>
  <div class="claim-profile-card"
       :class="{
         'claim-profile-card--clickable': clickable,
         'claim-profile-card--verified': verified,
       }"
       v-bind="rootBindings"
       v-on="rootListeners">
    <div class="claim-profile-card__doctor">
      <span class="claim-profile-card__doctor-avatar">
        <img v-if="doctor && doctor.profileurl"
             :src="doctor.profileurl"
             :alt="doctor.fullname">
      </span>
      <div class="claim-profile-card__doctor-text">
        <span class="claim-profile-card__doctor-name">{{ doctor ? doctor.fullname : '' }}</span>
        <span class="claim-profile-card__doctor-specialty">{{ doctor ? doctor.mainspecialty : '' }}</span>
      </div>
    </div>
    <div class="claim-profile-card__patient">
      <span class="claim-profile-card__patient-avatar">
        <img class="claim-profile-card__patient-avatar-icon" :src="patientAvatarIcon" alt="">
      </span>
      <span class="claim-profile-card__patient-name"
            :class="{'claim-profile-card__patient-name--verified': verified}">
        {{ displayName }}
      </span>
      <span class="claim-profile-card__badge"
            :class="verified
              ? 'claim-profile-card__badge--verified'
              : 'claim-profile-card__badge--unverified'">
        <span class="claim-profile-card__badge-text">
          {{ verified ? 'Verified' : 'Not Yet Verified' }}
        </span>
        <img v-if="verified"
             class="claim-profile-card__badge-icon"
             :src="verifiedIcon"
             alt="Verified">
      </span>
    </div>
  </div>
</template>

<script>
  import VerifiedIcon from '@/assets/icons/verified-fill.svg';
  import AttentionIcon from '@/assets/icons/attention-fill.svg';
  import PatientAvatarIcon from '@/assets/icons/patient-avatar-placeholder.svg';

  const MIN_MASK_LENGTH = 4;
  const MAX_MASK_LENGTH = 7;

  const randomMaskedSuffix = () => {
    const range = MAX_MASK_LENGTH - MIN_MASK_LENGTH + 1;
    const length = MIN_MASK_LENGTH + Math.floor(Math.random() * range);
    return '*'.repeat(length);
  };

  export default {
    name: 'claim-profile-card',
    props: {
      doctor: {type: Object, default: null},
      firstname: {type: String, default: ''},
      lastname: {type: String, default: ''},
      verified: {type: Boolean, default: false},
      clickable: {type: Boolean, default: false},
    },
    data() {
      return {
        verifiedIcon: VerifiedIcon,
        unverifiedIcon: AttentionIcon,
        patientAvatarIcon: PatientAvatarIcon,
        maskedSuffix: randomMaskedSuffix(),
      };
    },
    computed: {
      rootBindings() {
        return this.clickable ? {role: 'button', tabindex: 0} : {};
      },
      rootListeners() {
        return this.clickable
          ? {click: this.emitClick, keydown: this.handleKeydown}
          : {};
      },
      displayName() {
        if (this.verified) {
          return this.lastname
            ? `${this.firstname} ${this.lastname}`
            : this.firstname;
        }
        return `${this.firstname} ${this.maskedSuffix}`;
      },
    },
    methods: {
      emitClick() {
        this.$emit('click');
      },
      handleKeydown(event) {
        if (event.key === 'Enter') this.$emit('click');
      },
    },
  };
</script>

<style lang="scss" scoped>
  $color-gray-30: #ddddeb;
  $color-blue-light: #61beff;

  $card-shadow: 0 0 15px rgba(64, 61, 136, 0.1);

  .claim-profile-card {
    @extend %layout--flex-col;
    @include border-radius(14px);
    gap: space(s);
    background: $color-white;
    box-shadow: $card-shadow;
    padding: 12px;

    &--clickable {
      cursor: pointer;
      transition: transform 0.1s ease;

      &:hover {
        transform: translateY(-1px);
      }
    }

    &__doctor {
      @extend %layout--flex-row-center-x;
      gap: 8px;
    }

    &__doctor-avatar {
      @include border-radius(radius(circle));
      width: 26px;
      height: 26px;
      background: $color-gray-30;
      border: 1px solid $color-gray-30;
      overflow: hidden;
      flex-shrink: 0;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
    }

    &__doctor-text {
      @extend %layout--flex-col;
      flex: 1;
      min-width: 0;
      color: $color-black;
    }

    &__doctor-name {
      @extend %smd-font-family-bold;
      @extend %text-overflow-ellipsis;
      font-size: 13px;
      line-height: 1.1;
      color: $color-black;
      text-transform: none;
    }

    &__doctor-specialty {
      @extend %smd-font-family;
      margin-top: 2px;
      font-size: 10px;
      line-height: 1.1;
      color: $color-black;
      text-transform: none;
    }

    &__patient {
      display: grid;
      grid-template-columns: 34px 1fr auto;
      align-items: center;
      gap: 8px;
      padding-left: space(m);
    }

    &__patient-avatar {
      @include border-radius(radius(circle));
      width: 34px;
      height: 34px;
      background: $color-gray-40;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
    }

    &__patient-avatar-icon {
      width: 29px;
      height: 29px;
      display: block;
    }

    &__patient-name {
      @extend %smd-font-family-bold;
      @extend %text-overflow-ellipsis;
      font-size: font(l) - 1;
      line-height: 22px;
      color: $color-orange;
      letter-spacing: 0;
      text-transform: none;

      &--verified {
        color: $color-black;
      }
    }

    &__badge {
      display: inline-flex;
      align-items: center;
      gap: space(xs);

      &--unverified {
        @include border-radius(radius(small));
        background: #ffeae4;
        color: $color-orange;
        padding: space(xs) 6px;
      }

      &--verified {
        color: $color-blue-light;
      }
    }

    &__badge-icon {
      width: 24px;
      height: 24px;
      display: block;
    }

    &__badge-text {
      @extend %smd-font-family-bold;
      font-size: 10px;
      line-height: 1;
      text-transform: none;
    }
  }
</style>
